import {ApiResponse} from '../ApiClient'
import {GetAPIDomain, GetAPIHeaders} from '../Helper'

// Kept separate from ProjectsClient.ts / UsersClient.ts so the guest bundle never
// pulls in admin-facing API calls.
export class ProjectGuestPortalClient {
  static async Activate(code: string, password: string) {
    const response = await fetch((await GetAPIDomain()) + '/connect/project-guest/activate', {
      method: 'POST',
      headers: new Headers({
        'content-type': 'application/json',
        'x-account-discovery': 'x-account-discovery',
      }),
      body: JSON.stringify({
        code,
        password,
      }),
    })

    return await response.json()
  }

  // Same endpoints/shape as UsersClient.BeginPasswordReset/CompletePasswordReset, duplicated
  // here rather than imported so the guest bundle never pulls in admin-facing API calls. These
  // are plain [AllowAnonymous] endpoints (not admin-gated), so there's nothing guest-unsafe
  // about reusing them directly for a project guest's own password reset.
  //
  // accountId (resolved from the company short code) scopes the email lookup to that account:
  // a guest's address can also belong to a user in another account, and without it the API takes
  // whichever user it finds first.
  static async BeginPasswordReset(email: string, accountId: string) {
    const response = await fetch((await GetAPIDomain()) + '/reset-password/begin', {
      method: 'POST',
      headers: new Headers({
        'content-type': 'application/json',
        'x-account-discovery': 'x-account-discovery',
        'x-account-id': accountId,
      }),
      body: JSON.stringify({email}),
    })

    // Accounts without automatic tenant discovery get a 202 with no body back, which
    // response.json() can't parse. The reset has still been started.
    const text = await response.text()
    const data = text ? JSON.parse(text) : {}

    return {...data, successful: response.ok && data.successful !== false}
  }

  static async CompletePasswordReset(code: string, password: string) {
    const response = await fetch((await GetAPIDomain()) + '/reset-password/complete', {
      method: 'POST',
      headers: new Headers({
        'content-type': 'application/json',
        'x-account-discovery': 'x-account-discovery',
      }),
      body: JSON.stringify({code, password}),
    })

    return await response.json()
  }

  // ── Login (step 1: credentials, step 2: emailed OTP) ───────────────────────
  // Mirrors AuthCRUD.login()'s x-account-id/credentials pattern; the accountId is expected
  // to already be in localStorage (ProjectGuestLogin calls the shared discoverCurrentAccount first,
  // same tenant-discovery step the CRM login uses).

  static async LogIn(email: string, password: string) {
    const accountId = localStorage.getItem('accountId') || ''
    const authDomain = await GetAPIDomain()

    const response = await fetch(authDomain + '/connect/project-guest/log-in', {
      method: 'POST',
      headers: {
        'x-account-id': accountId,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({email, password}),
    })

    const data = await response.json()

    if (response.ok) {
      localStorage.setItem('domain-auth', authDomain || '')
      return {successful: data.successful, requiresOtp: data.requiresOtp}
    }

    return {successful: false, requiresOtp: false}
  }

  static async VerifyOtp(email: string, code: string) {
    const accountId = localStorage.getItem('accountId') || ''
    const authDomain = await GetAPIDomain()

    const response = await fetch(authDomain + '/connect/project-guest/verify-otp', {
      method: 'POST',
      headers: {
        'x-account-id': accountId,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({email, code}),
    })

    if (response.ok) {
      localStorage.setItem('domain-auth', authDomain || '')
      localStorage.setItem('cookie-auth', 'true')
      return {successful: true}
    }

    return {successful: false}
  }

  static async ResendOtp(email: string) {
    const accountId = localStorage.getItem('accountId') || ''
    const authDomain = await GetAPIDomain()

    const response = await fetch(authDomain + '/connect/project-guest/resend-otp', {
      method: 'POST',
      headers: {
        'x-account-id': accountId,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({email}),
    })

    return {successful: response.ok}
  }

  // ── Portal data (all cookie-authenticated, wrap /projects/*, Phase 3) ─────

  // Every active project the guest is on, whatever its start/end dates — one that hasn't
  // started or has already finished still belongs on the list (My Project badges it as Not
  // Started / Completed, and booking stays limited to the project's own date range).
  static async GetMyProjects() {
    return await ApiResponse.Create(async () =>
      fetch((await GetAPIDomain()) + '/projects/me?allDates=true', {
        credentials: 'include',
        headers: GetAPIHeaders(),
      })
    )
  }

  static async GetProject(projectId: string) {
    return await ApiResponse.Create(async () =>
      fetch((await GetAPIDomain()) + '/projects/' + projectId, {
        credentials: 'include',
        headers: GetAPIHeaders(),
      })
    )
  }

  static async GetMyProfile() {
    return await ApiResponse.Create(async () =>
      fetch((await GetAPIDomain()) + '/projects/me/profile', {
        credentials: 'include',
        headers: GetAPIHeaders(),
      })
    )
  }

  // The account's day-state colours. Lives on /projects rather than /account-settings because
  // ProjectGuestRoutePolicy confines a guest's cookie to ProjectsController, and a 403
  // anywhere else tears the session down (see ApiResponse.Create).
  static async GetTheme() {
    return await ApiResponse.Create(async () =>
      fetch((await GetAPIDomain()) + '/projects/me/theme', {
        credentials: 'include',
        headers: GetAPIHeaders(),
      })
    )
  }

  static async GetAssetTypeName() {
    return await ApiResponse.Create(async () =>
      fetch((await GetAPIDomain()) + '/projects/me/asset-type-name', {
        credentials: 'include',
        headers: GetAPIHeaders(),
      })
    )
  }

  static async GetMyBookings() {
    return await ApiResponse.Create(async () =>
      fetch((await GetAPIDomain()) + '/projects/me/bookings', {
        credentials: 'include',
        headers: GetAPIHeaders(),
      })
    )
  }

  static async GetAvailableAssets(projectId: string, date: string) {
    return await ApiResponse.Create(async () =>
      fetch(
        (await GetAPIDomain()) +
          '/projects/' +
          projectId +
          '/assets/available?date=' +
          encodeURIComponent(date),
        {
          credentials: 'include',
          headers: GetAPIHeaders(),
        }
      )
    )
  }

  // Books one of the project's assets at a chosen time: the guest-reachable mirror of the
  // Teams client's POST /workday-plans/check-in. Comes back as one CheckInPlannerResult per
  // booking, so a rejection carries its own reason.
  static async BookAsset(
    projectId: string,
    assetId: string,
    date: string,
    startTime: string,
    duration: number,
    ignoreConnectRule: boolean
  ) {
    return await ApiResponse.Create(async () =>
      fetch((await GetAPIDomain()) + '/projects/' + projectId + '/check-ins/planner', {
        credentials: 'include',
        method: 'POST',
        headers: GetAPIHeaders(),
        body: JSON.stringify({assetId, date, startTime, duration, ignoreConnectRule}),
      })
    )
  }

  static async SwitchAsset(projectId: string, assetId: string, date: string) {
    return await ApiResponse.Create(async () =>
      fetch((await GetAPIDomain()) + '/projects/' + projectId + '/switch-asset', {
        credentials: 'include',
        method: 'POST',
        headers: GetAPIHeaders(),
        body: JSON.stringify({assetId, date}),
      })
    )
  }

  static async ReleaseCheckIn(projectId: string, id: string, isDelete: boolean = false) {
    return await ApiResponse.Create(async () =>
      fetch((await GetAPIDomain()) + '/projects/' + projectId + '/check-ins/release', {
        credentials: 'include',
        method: 'POST',
        headers: GetAPIHeaders(),
        body: JSON.stringify({id, isDelete}),
      })
    )
  }

  static async ExtendCheckIn(projectId: string, id: string, duration: number) {
    return await ApiResponse.Create(async () =>
      fetch((await GetAPIDomain()) + '/projects/' + projectId + '/check-ins/extend', {
        credentials: 'include',
        method: 'POST',
        headers: GetAPIHeaders(),
        body: JSON.stringify({id, duration}),
      })
    )
  }

  static async EarlyCheckIn(projectId: string, checkInId: string) {
    return await ApiResponse.Create(async () =>
      fetch(
        (await GetAPIDomain()) +
          '/projects/' +
          projectId +
          '/check-ins/' +
          checkInId +
          '/early-checkin',
        {
          credentials: 'include',
          method: 'POST',
          headers: GetAPIHeaders(),
        }
      )
    )
  }

  // ── Week planner (day cards on My Project) ────────────────────────────────

  static async GetWeekView(projectId: string, startDate: string) {
    return await ApiResponse.Create(async () =>
      fetch(
        (await GetAPIDomain()) +
          '/projects/' +
          projectId +
          '/me/week-view?startDate=' +
          encodeURIComponent(startDate),
        {
          credentials: 'include',
          headers: GetAPIHeaders(),
        }
      )
    )
  }

  // The project members on site for one day, opened from a day card's doughnut.
  static async GetDayOnSiteUsers(projectId: string, date: string) {
    return await ApiResponse.Create(async () =>
      fetch(
        (await GetAPIDomain()) +
          '/projects/' +
          projectId +
          '/me/day-users?date=' +
          encodeURIComponent(date),
        {
          credentials: 'include',
          headers: GetAPIHeaders(),
        }
      )
    )
  }

  static async GetPlanReasons(projectId: string) {
    return await ApiResponse.Create(async () =>
      fetch((await GetAPIDomain()) + '/projects/' + projectId + '/me/reasons', {
        credentials: 'include',
        headers: GetAPIHeaders(),
      })
    )
  }

  static async SetDayPlan(
    projectId: string,
    date: string,
    state: string,
    offSiteReasonId?: string | null,
    notWorkingReasonId?: string | null,
    // On-site only, and only ever one of the project's own sites; the API rejects anything
    // else. Null falls back to the guest's default site, as it did before the picker existed.
    siteId?: string | null
  ) {
    return await ApiResponse.Create(async () =>
      fetch((await GetAPIDomain()) + '/projects/' + projectId + '/me/day-plan', {
        credentials: 'include',
        method: 'POST',
        headers: GetAPIHeaders(),
        body: JSON.stringify({
          date,
          state,
          siteId: siteId || null,
          offSiteReasonId: offSiteReasonId || null,
          notWorkingReasonId: notWorkingReasonId || null,
        }),
      })
    )
  }

  // Undoes a day: releases whatever the guest had booked out of the project's pool for that
  // date and removes the plan itself, the way re-clicking a chosen action does in Teams.
  static async ClearDayPlan(projectId: string, date: string) {
    return await ApiResponse.Create(async () =>
      fetch(
        (await GetAPIDomain()) +
          '/projects/' +
          projectId +
          '/me/day-plan?date=' +
          encodeURIComponent(date),
        {
          credentials: 'include',
          method: 'DELETE',
          headers: GetAPIHeaders(),
        }
      )
    )
  }

  static async ChangePassword(currentPassword: string, newPassword: string) {
    return await ApiResponse.Create(async () =>
      fetch((await GetAPIDomain()) + '/projects/me/change-password', {
        credentials: 'include',
        method: 'POST',
        headers: GetAPIHeaders(),
        body: JSON.stringify({currentPassword, newPassword}),
      })
    )
  }
}
